import React from "react";
import {
  useQuery,
  useMutation,
  // useQueryClient,
  useIsFetching,
} from "react-query";
import { Network, Loader2 } from "lucide-react";
import { Input } from "@components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@components/ui/popover";
import CustomTooltip from "./CustomTooltip";
import { RadioGroup, RadioGroupItem } from "@components/ui/radio-group";
import { Label } from "@components/ui/label";
import { Button } from "@components/ui/button";
import { toast } from "@components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import CustomWidget from "@components/common/Widget";
import TableLoader from "@components/loaders/TableLoader";
import QuadrantLoader from "@components/loaders/QuadrantLoader";
import DataTable from "@components/common/DataTable";
import QuadrantChart from "@components/common/charts/QuadrantChart";
import { QuadrantDataItemProps } from "@components/common/charts/ChartTypes";
import { AssetEntityInspectorContent } from "@pages/homepage/asset-auto-discovery/AssetEntityInspector";
import UserInvestigator from "@components/common/UserInvestigator";
import {
  getSystemArchitectureInfo,
  setDistributedSystemIpSession,
  getDistributedDashboardInfo,
  // getDistributedDashboardData,
  uploadLicenseToSensor,
} from "@services/api/commonApis";
import { systemArchitectureInfoProp } from "./CommonTypes";

const MsspView = () => {
  // const queryClient = useQueryClient();
  const isFetching = useIsFetching();
  const [msspView, setMsspView] = React.useState(false);
  const [fetchingLoader, setFetchingLoader] = React.useState(false);
  const reloadPageRef = React.useRef(true);
  const [msspDash, setMsspDash] = React.useState(false);
  const [time, setTime] = React.useState("day");
  const [quadrantData, setQuadrantData] = React.useState(
    [] as QuadrantDataItemProps[],
  );
  const [userId, setUserId] = React.useState("");
  const [macAddress, setMacAddress] = React.useState("");
  const [licenseDialog, setLicenseDialog] = React.useState(false);
  const [licenseSystemId, setLicenseSystemId] = React.useState("");
  const [licenseSensorIp, setLicenseSensorIp] = React.useState("");
  const [licenseFile, setLicenseFile] = React.useState<File | null>(null);

  const uploadLicenseMutation = useMutation(uploadLicenseToSensor, {
    onSuccess: (data) => {
      if (data.status == 1) {
        toast({
          title: "Success",
          description: "License uploaded successfully",
          variant: "success",
        });
        setLicenseDialog(false);
        setLicenseFile(null);
        setLicenseSystemId("");
        setLicenseSensorIp("");
      } else {
        toast({
          title: "Error",
          description: data.message || "Failed to upload license",
          variant: "destructive",
        });
      }
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Something went wrong while uploading the license",
        variant: "destructive",
      });
    },
  });

  const { data: systemArchitectureInfo } = useQuery<systemArchitectureInfoProp>(
    ["systemArchitectureInfo"],
    ({ signal }) => getSystemArchitectureInfo(signal),
  );

  const {
    data: msspDashData,
    isLoading: msspDataLoading,
    isRefetching: msspDataRefetching,
    refetch: refetchMsspData,
  } = useQuery(
    ["msspDashData"],
    ({ signal }) => getDistributedDashboardInfo(time, signal),
    {
      enabled: msspDash,
    },
  );

  React.useEffect(() => {
    if (fetchingLoader && !isFetching) {
      setFetchingLoader(false);
    }
  }, [isFetching]);

  React.useEffect(() => {
    if (time && msspDash) {
      refetchMsspData();
    }
  }, [time]);

  const setDistributedSystemIpSessionMutation = useMutation(
    setDistributedSystemIpSession,
    {
      onSuccess: (json) => {
        if (json.status == "success") {
          if (!reloadPageRef.current) return;
          toast({
            title: "Success",
            description: "Your request has been submitted",
            variant: "success",
          });
          // reload the page
          // queryClient.invalidateQueries({ refetchInactive: true });
          setFetchingLoader(true);
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        } else if (json.status == "invalid_license") {
          setLicenseSystemId(json.system_id || "");
          setLicenseSensorIp(json.sensor_ip || "");
          setLicenseDialog(true);
        } else if (json.status == "incompatible_version") {
          toast({
            title: "Error",
            description:
              "The selected system is running a diffrent version from master. Please update to the same version in settings page.",
            variant: "destructive",
            duration: 60000,
          });
        } else {
          toast({
            title: "Error",
            description: "An error occured!",
            variant: "destructive",
          });
        }
      },
      onError: () => {
        toast({
          title: "Error",
          description: "An error occured!",
          variant: "destructive",
        });
      },
    },
  );

  React.useEffect(() => {
    if (
      systemArchitectureInfo &&
      systemArchitectureInfo.distributedSystemsType == "mssp"
    ) {
      setMsspView(true);
    }
    return () => {
      setMsspView(false);
      sessionStorage.removeItem("distributed_systems_initial_ip");
    };
  }, [systemArchitectureInfo]);

  React.useEffect(() => {
    if (msspDashData) {
      const quadTemp: QuadrantDataItemProps[] = [];
      for (const data of msspDashData.all_anomalies) {
        quadTemp.push({
          name: data.host || data.ipAddr || data.user,
          type: data.anomalyType || data.name,
          ip: data.ipAddr,
          count: data.score,
          score: data.count,
          Y: data.count,
          X: data.score,
          mac: data.macAddr,
          username: data.user || "",
        });
      }
      setQuadrantData(quadTemp);
    }
  }, [msspDashData]);

  const handleMutationCompletion = async (ls_system: string) => {
    reloadPageRef.current = false;
    await setDistributedSystemIpSessionMutation.mutateAsync({
      ip: ls_system,
      distributed_systems_type:
        systemArchitectureInfo?.distributedSystemsType || "",
      device_name: "",
    });
  };

  React.useEffect(() => {
    if (msspDash) {
      sessionStorage.setItem(
        "distributed_systems_initial_ip",
        systemArchitectureInfo?.distributedSystemsSession || "",
      );
    } else {
      const initialIp = sessionStorage.getItem(
        "distributed_systems_initial_ip",
      );
      if (initialIp) {
        handleMutationCompletion(initialIp).then(() => {
          sessionStorage.removeItem("distributed_systems_initial_ip");
        });
      }
    }
  }, [msspDash]);

  const handleMsspChange = (value: string) => {
    reloadPageRef.current = true;
    const sys = systemArchitectureInfo?.distributedSystems.find(
      (s) => s.device_ip == value,
    );
    if (systemArchitectureInfo && sys) {
      setDistributedSystemIpSessionMutation.mutate({
        ip: sys.device_ip,
        distributed_systems_type: systemArchitectureInfo.distributedSystemsType,
        device_name: sys.device_name,
      });
    }
  };
  if (!msspView) return;

  const anomalyTableColumns = [
    {
      header: "Category",
      accessor: "category",
    },
    {
      header: "Host/User",
      accessor: "host",
      type: "component",
    },
    // {
    //   header: "Un/Managed",
    //   accessor: "unmanaged",
    // },
    {
      header: "Latest Anomaly Type",
      accessor: "latestAnomalyType",
      type: "component",
    },
    {
      header: "Description",
      accessor: "description",
    },
    {
      header: "Time",
      accessor: "time",
      sortingFn: (rowA: any, rowB: any) => {
        if (
          rowA.original.time &&
          rowB.original.time &&
          rowA.original.time != "" &&
          rowB.original.time != ""
        ) {
          return (
            new Date(rowA.original.time.split(",")[1].trim()).getTime() -
            new Date(rowB.original.time.split(",")[1].trim()).getTime()
          );
        } else {
          return 0;
        }
      },
    },
    {
      header: "Total Score",
      accessor: "totalscore",
      type: "component",
      sortingFn: (rowA: any, rowB: any) => {
        return rowA.original.score - rowB.original.score;
      },
    },
    {
      header: "LS System",
      accessor: "ls_system",
    },
  ];

  const filteredAnomalyTable = msspDashData?.all_anomalies
    .filter((data: any) => {
      return (data.host && data.category) || data.user;
    })
    .map((data: any) => {
      const ls_system_ip =
        msspDashData?.ls_systems.find((sys: any) => sys.name == data.ls_system)
          ?.ip || data.ls_system;
      return {
        category: data.category,
        host: (
          <span
            className="cursor-pointer text-primary"
            onClick={() => {
              if (data.user) {
                handleAssetUserClick(ls_system_ip, undefined, data.user);
              } else {
                handleAssetUserClick(ls_system_ip, data.smac, undefined);
              }
            }}
          >
            {data.host || data.ipAddr || data.user}
          </span>
        ),
        unmanaged: data.unmanaged,
        latestAnomalyType: data.type || data.anomalyType,
        description: data.desc,
        time: data.time,
        totalscore: data.score,
        ls_system: data.ls_system,
      };
    });
  const handleAssetUserClick = (
    ls_system: string,
    mac?: string,
    user?: string,
  ) => {
    handleMutationCompletion(ls_system).then(() => {
      if (mac) {
        setMacAddress(mac);
      } else if (user) {
        setUserId(user);
      }
    });
  };

  const timeFrames = [
    ["day", "Last 24 Hours"],
    ["3day", "Last 3 Days"],
    ["7day", "Last 7 Days"],
    ["month", "Last 30 Days"],
    ["2month", "Last 60 Days"],
  ];

  const masterIp =
    systemArchitectureInfo?.distributedSystemsSession ||
    systemArchitectureInfo?.distributedSystems?.filter(
      (sys) => sys.device_type === "master",
    )[0]?.device_ip;
  return (
    <>
      <Popover>
        <PopoverTrigger asChild>
          <div>
            <CustomTooltip text="Distributed System">
              {setDistributedSystemIpSessionMutation.isLoading ||
              fetchingLoader ? (
                <Loader2 className="animate-spin" size={28} />
              ) : (
                <Network className="cursor-pointer" size={28} />
              )}
            </CustomTooltip>
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-46 max-h-[60vh] overflow-y-auto">
          {systemArchitectureInfo?.distributedSystemsCombineDashboard ==
            "1" && (
            <Button
              variant="ghost"
              className="mb-2"
              onClick={() => setMsspDash(true)}
            >
              Distributed Dashboard
            </Button>
          )}
          <RadioGroup
            onValueChange={handleMsspChange}
            disabled={setDistributedSystemIpSessionMutation.isLoading}
            defaultValue={masterIp}
          >
            {systemArchitectureInfo?.distributedSystems.map((sys) => {
              return (
                <div
                  key={sys.device_name}
                  className="flex items-center space-x-2 p-2 hover:bg-secondary hover:cursor-pointer"
                >
                  <RadioGroupItem value={sys.device_ip} id={sys.device_ip} />
                  <Label
                    htmlFor={sys.device_ip}
                    className="hover:bg-secondary hover:cursor-pointer"
                  >{`${sys.device_name} (${sys.device_ip})`}</Label>
                </div>
              );
            })}
          </RadioGroup>
        </PopoverContent>
      </Popover>
      {msspDash && (
        <Dialog open={msspDash} onOpenChange={setMsspDash}>
          <DialogContent className="sm:max-w-[90vw] sm:max-h-[90vh] overflow-y-auto overflow-x-hidden">
            <DialogHeader className="flex-row justify-between">
              <div className="space-y-2">
                <DialogTitle>Distributed Dashboard</DialogTitle>
                <DialogDescription>
                  You are now viewing the distributed dashboard.
                </DialogDescription>
              </div>
              <div className="mr-8">
                <Select
                  onValueChange={(e) => setTime(e)}
                  value={time}
                  defaultValue="day"
                >
                  <SelectTrigger className="w-full rounded-md">
                    <SelectValue placeholder="Time Frame..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {timeFrames.map(([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div
                className="w-full"
                style={{ height: "50vh" }}
                id="msspquadChart"
              >
                {msspDataLoading || msspDataRefetching ? (
                  <div className="px-2">
                    <QuadrantLoader />
                  </div>
                ) : (
                  <QuadrantChart
                    data={quadrantData}
                    parentid="msspquadChart"
                    // handleClick={setFilter}
                  />
                )}
              </div>

              <div className="w-full p-2 rounded-md shadow-xl bg-primary-background h-full">
                <h2 className="font-bold m-3">Anomaly List</h2>
                {msspDataLoading || msspDataRefetching ? (
                  <TableLoader colCount={7} />
                ) : (
                  <DataTable
                    cols={anomalyTableColumns}
                    data={filteredAnomalyTable}
                    className="h-full"
                    totalCount={filteredAnomalyTable?.length}
                    countPerPageSelection={true}
                  />
                )}
              </div>

              <div>
                {msspDashData?.ls_systems && (
                  <div className="w-full p-2 rounded-md shadow-xl h-full overflow-y-auto">
                    {msspDashData.ls_systems.map((device: any) => {
                      return (
                        <div key={device.name} className="">
                          <h2 className="font-bold m-3 flex gap-4">
                            <Network />
                            {device.ip} - {device.name}
                          </h2>
                          <div className="grid grid-cols-4 gap-2">
                            <CustomWidget
                              title="Top Anomalies"
                              className="col-span-1"
                              info="Top Anomalies"
                            >
                              <div className="h-80 overflow-y-auto">
                                {msspDashData.top_anomalies[device.ip]
                                  ?.length == 0 && (
                                  <div className="text-sm p-2">
                                    Not Available
                                  </div>
                                )}
                                {msspDashData.top_anomalies[device.ip]?.map(
                                  (anomaly: any) => {
                                    return (
                                      <div
                                        key={anomaly.anomaly_id}
                                        className="flex items-center justify-between space-x-2 hover:bg-secondary border"
                                      >
                                        <div className="text-sm font-bold p-2">
                                          {anomaly.anomaly_type}
                                        </div>
                                        <div className="text-sm text-center border-l p-2 px-4">
                                          Count
                                          <br />
                                          <span className="text-primary text-base">
                                            {anomaly.count}
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  },
                                ) || "Not Available"}
                              </div>
                            </CustomWidget>
                            <CustomWidget
                              title="Top Entities by Risk"
                              className="col-span-1"
                              info="Top Entities by Risk"
                            >
                              <div className="h-80 overflow-y-auto">
                                {msspDashData.top_entity_anomalies[device.ip]
                                  ?.length == 0 && (
                                  <div className="text-sm p-2">
                                    Not Available
                                  </div>
                                )}
                                {msspDashData.top_entity_anomalies[
                                  device.ip
                                ]?.map((anomaly: any) => {
                                  if (!anomaly.host && !anomaly.smac)
                                    return null;
                                  return (
                                    <div
                                      key={anomaly.id}
                                      className="flex justify-between items-center space-x-2 hover:bg-secondary border"
                                    >
                                      <div
                                        className="text-sm font-bold p-2 hover:cursor-pointer"
                                        onClick={() => {
                                          handleAssetUserClick(
                                            device.ip,
                                            anomaly.smac,
                                            undefined,
                                          );
                                        }}
                                      >
                                        {anomaly.host || anomaly.smac}
                                      </div>
                                      <div className="text-sm text-center border-l p-2 px-4">
                                        Score
                                        <br />
                                        <span className="text-primary text-base">
                                          {anomaly.score || 0}
                                        </span>
                                      </div>
                                    </div>
                                  );
                                }) || "Not Available"}
                              </div>
                            </CustomWidget>
                            <CustomWidget
                              title="Top Users by Risk"
                              className="col-span-1"
                              info="Top Users by Risk"
                            >
                              <div className="h-80 overflow-y-auto">
                                {msspDashData.top_user_anomalies[device.ip]
                                  ?.length == 0 && (
                                  <div className="text-sm p-2">
                                    Not Available
                                  </div>
                                )}
                                {msspDashData.top_user_anomalies[
                                  device.ip
                                ]?.map((anomaly: any) => {
                                  return (
                                    <div
                                      key={anomaly.id}
                                      className="flex justify-between items-center space-x-2 hover:bg-secondary border"
                                    >
                                      <div
                                        className="text-sm font-bold p-2 hover:cursor-pointer"
                                        onClick={() => {
                                          handleAssetUserClick(
                                            device.ip,
                                            undefined,
                                            anomaly.user,
                                          );
                                        }}
                                      >
                                        {anomaly.user || anomaly.displayName}
                                      </div>
                                      <div className="text-sm text-center border-l p-2 px-4">
                                        Score <br />
                                        <span className="text-primary text-base">
                                          {anomaly.score || 0}
                                        </span>
                                      </div>
                                    </div>
                                  );
                                }) || "Not Available"}
                              </div>
                            </CustomWidget>
                            <CustomWidget
                              title="Statistics"
                              className="col-span-1"
                              info="Statistics"
                            >
                              <div className="h-80 overflow-y-auto flex flex-col [&>h4]:p-2">
                                <h4>
                                  Total Entities -{" "}
                                  <span className="text-primary text-base">
                                    {msspDashData.total_entities[device.ip] ||
                                      0}
                                  </span>
                                </h4>
                                <h4>
                                  Total Users -{" "}
                                  <span className="text-primary text-base">
                                    {msspDashData.total_users[device.ip] || 0}
                                  </span>
                                </h4>
                                <h4>
                                  Last refreshed time -{" "}
                                  <span className="text-primary text-base">
                                    {msspDashData.last_updated_time[
                                      device.ip
                                    ] || "-"}
                                  </span>
                                </h4>
                              </div>
                            </CustomWidget>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      {userId && (
        <UserInvestigator
          openState={true}
          onClose={() => {
            setUserId("");
          }}
          account={userId}
        />
      )}
      {macAddress && (
        <AssetEntityInspectorContent
          open={true}
          onClose={() => setMacAddress("")}
          mac={macAddress}
          filter_time={time}
        />
      )}
      {licenseDialog && (
        <Dialog
          open={licenseDialog}
          onOpenChange={(open) => {
            setLicenseDialog(open);
            if (!open) {
              setLicenseFile(null);
              setLicenseSystemId("");
              setLicenseSensorIp("");
            }
          }}
        >
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Upload License</DialogTitle>
              <DialogDescription>
                This sensor requires a valid license to connect. Please upload
                the license document.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div>
                <Label>System ID</Label>
                <textarea
                  readOnly
                  value={licenseSystemId}
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background mt-2"
                />
              </div>
              <div>
                <Label>License Document</Label>
                <Input
                  type="file"
                  className="file:text-gray-500 mt-2"
                  onChange={(e) => {
                    const files = e.target.files;
                    setLicenseFile(files ? files[0] : null);
                  }}
                />
              </div>
              <div className="flex justify-end">
                <Button
                  disabled={uploadLicenseMutation.isLoading || !licenseFile}
                  onClick={() => {
                    if (!licenseFile) return;
                    if (licenseFile.type !== "") {
                      toast({
                        title: "Error",
                        description: "Please upload a valid license file",
                        variant: "destructive",
                      });
                      return;
                    }
                    const formData = new FormData();
                    formData.append("file", licenseFile);
                    formData.append("sensor_ip", licenseSensorIp);
                    uploadLicenseMutation.mutate(formData);
                  }}
                >
                  {uploadLicenseMutation.isLoading ? (
                    <Loader2 className="animate-spin mr-2" size={16} />
                  ) : null}
                  Upload License
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default MsspView;
